/**
 * Data layer for `click-level-detail-reports`. Thin typed fetch wrappers over
 * `/api/v1/click-level-detail-reports`. Raw `fetch()` is confined to this file
 * per the D6 dash contract.
 *
 * Backend shape:
 *   GET /clicks?since=&until=&campaign_id=&country=&per_page=&cursor=
 *     -> { data: ClickDetailView[], meta: { per_page, total, next_cursor } }
 *   GET /stats
 *     -> { total, by_status: { pending, accepted, rejected, duplicate, bot } }
 *
 * All requests include `x-tenant-id: 't1'` per dispatch spec; the tenant
 * middleware on the worker uses this to scope the result.
 */

export const ALLOWED_STATUSES = [
	"pending",
	"accepted",
	"rejected",
	"duplicate",
	"bot",
] as const;
export type ClickStatus = (typeof ALLOWED_STATUSES)[number];

export interface ClickDetailView {
	click_id: string;
	ts: number;
	ip: string | null;
	ua: string | null;
	country: string | null;
	device_type: string | null;
	os: string | null;
	browser: string | null;
	campaign_id: string | null;
	affiliate_id: string | null;
	placement_id: string | null;
	creative_id: string | null;
	referrer: string | null;
	landing_url: string | null;
	attributed_conversion_id: string | null;
	status: ClickStatus;
	created_at: number;
	updated_at: number;
}

export interface ListClicksFilters {
	since: number | null;
	until: number | null;
	campaign_id: string | null;
	country: string | null;
	per_page?: number;
}

export interface ListClicksResponse {
	data: ClickDetailView[];
	meta: {
		per_page: number;
		total: number;
		next_cursor: string | null;
	};
}

export interface StatsResponse {
	total: number;
	by_status: Record<ClickStatus, number>;
}

const BASE = "/api/v1/click-level-detail-reports";

function withTenant(init?: RequestInit): RequestInit {
	const headers = new Headers(init?.headers ?? {});
	headers.set("accept", "application/json");
	headers.set("x-tenant-id", "t1");
	return { ...init, headers, credentials: "include" };
}

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

function buildClicksQuery(f: ListClicksFilters): string {
	const p = new URLSearchParams();
	if (f.since !== null && Number.isFinite(f.since)) p.set("since", String(f.since));
	if (f.until !== null && Number.isFinite(f.until)) p.set("until", String(f.until));
	if (f.campaign_id) p.set("campaign_id", f.campaign_id);
	if (f.country) p.set("country", f.country);
	if (f.per_page && Number.isFinite(f.per_page)) p.set("per_page", String(f.per_page));
	const qs = p.toString();
	return qs ? `?${qs}` : "";
}

export async function listClicks(f: ListClicksFilters): Promise<ListClicksResponse> {
	const url = `${BASE}/clicks${buildClicksQuery(f)}`;
	const res = await fetch(url, withTenant({ method: "GET" }));
	return parseOrThrow<ListClicksResponse>(res);
}

export async function getStats(): Promise<StatsResponse> {
	const res = await fetch(`${BASE}/stats`, withTenant({ method: "GET" }));
	return parseOrThrow<StatsResponse>(res);
}

export const clickLevelDetailReportsQueryKeys = {
	all: ["click-level-detail-reports"] as const,
	list: (filters: ListClicksFilters) =>
		[...clickLevelDetailReportsQueryKeys.all, "list", filters] as const,
	stats: () => [...clickLevelDetailReportsQueryKeys.all, "stats"] as const,
	detail: (id: string) =>
		[...clickLevelDetailReportsQueryKeys.all, "detail", id] as const,
};

// ---------------------------------------------------------------------------
// Time helpers — pure, easy to test.
// ---------------------------------------------------------------------------

export function nowSec(): number {
	return Math.floor(Date.now() / 1000);
}

export function daysAgoSec(days: number): number {
	return nowSec() - days * 86400;
}

export function secFromLocalDate(v: string): number | null {
	if (!v) return null;
	// datetime-local or date input -> UTC seconds
	const t = Date.parse(v);
	if (!Number.isFinite(t)) return null;
	return Math.floor(t / 1000);
}

export function localDateFromSec(sec: number): string {
	const d = new Date(sec * 1000);
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function formatUtcTs(sec: number): string {
	if (!Number.isFinite(sec)) return "—";
	const d = new Date(sec * 1000);
	const pad = (n: number) => String(n).padStart(2, "0");
	return (
		`${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
		`${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`
	);
}

export function truncate(s: string | null, max: number): string {
	if (!s) return "—";
	if (s.length <= max) return s;
	return s.slice(0, max - 1) + "…";
}

export function uniqueIps(rows: ClickDetailView[]): number {
	const set = new Set<string>();
	for (const r of rows) {
		if (r.ip) set.add(r.ip);
	}
	return set.size;
}

export function countSince(rows: ClickDetailView[], sinceSec: number): number {
	let n = 0;
	for (const r of rows) {
		if (r.ts >= sinceSec) n += 1;
	}
	return n;
}

/**
 * Build a dense [startDaySec -> count] series spanning `[sinceSec, untilSec]`
 * bucketed per UTC day. Missing days are filled with 0 so the sparkline path
 * has a monotonic x-axis with no jumps.
 */
export interface SeriesPoint {
	day: number; // UTC midnight unix-seconds
	count: number;
}

export function dailySeries(
	rows: ClickDetailView[],
	sinceSec: number,
	untilSec: number,
): SeriesPoint[] {
	if (!Number.isFinite(sinceSec) || !Number.isFinite(untilSec) || untilSec < sinceSec) {
		return [];
	}
	const startDay = Math.floor(sinceSec / 86400) * 86400;
	const endDay = Math.floor(untilSec / 86400) * 86400;
	const buckets = new Map<number, number>();
	for (let d = startDay; d <= endDay; d += 86400) buckets.set(d, 0);
	for (const r of rows) {
		const day = Math.floor(r.ts / 86400) * 86400;
		if (buckets.has(day)) buckets.set(day, (buckets.get(day) ?? 0) + 1);
	}
	return Array.from(buckets.entries())
		.sort((a, b) => a[0] - b[0])
		.map(([day, count]) => ({ day, count }));
}
